import React, { useContext, useRef } from 'react'
import { useEffect, useState } from 'react'
import { Input, Select, Modal, Form, Button, Typography, Table, Popconfirm, notification, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { GLOBAL_CLIENT } from '../../helpers/GlobalClient'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../hooks'
import styled from 'styled-components'
import { PlusOutlined } from '@ant-design/icons'
import type { FormInstance } from 'antd/es/form'
import { debounce, size } from 'lodash'
import { formatCurrency, showMessageError } from 'src/helpers/functions'
import { getStatusOrder, DATE_TIME_FORMAT_CLIENT_short, DELIVERY_CONST, GKE_SERVICES } from 'src/helpers/constants'
import moment from 'moment'
import { Loading } from 'src/common'
import useWindowDimensions from 'src/common/useWindowDimensions'

const formItemLayout = {
   labelCol: {
      xs: { span: 24 },
      sm: { span: 8 }
   },
   wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 }
   }
}

export const ModalWrapper = styled.div`
   .ant-table-wrapper {
      overflow: scroll !important;
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f1f1f1;
   }

   .ant-table {
      overflow: visible !important;
   }

   .ant-table-content {
      overflow: scroll !important;
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f1f1f1;
   }

   .ant-table-body {
      overflow: scroll !important;
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f1f1f1;
   }

   .ant-table-wrapper::-webkit-scrollbar,
   .ant-table-content::-webkit-scrollbar,
   .ant-table-body::-webkit-scrollbar {
      width: 12px !important;
      height: 12px !important;
      -webkit-appearance: none;
   }

   .ant-table-wrapper::-webkit-scrollbar-track,
   .ant-table-content::-webkit-scrollbar-track,
   .ant-table-body::-webkit-scrollbar-track {
      background: #f1f1f1 !important;
      border-radius: 6px !important;
   }

   .ant-table-wrapper::-webkit-scrollbar-thumb,
   .ant-table-content::-webkit-scrollbar-thumb,
   .ant-table-body::-webkit-scrollbar-thumb {
      background-color: #c1c1c1 !important;
      border-radius: 6px !important;
      border: 2px solid #f1f1f1 !important;
   }

   .ant-table-wrapper::-webkit-scrollbar-thumb:hover,
   .ant-table-content::-webkit-scrollbar-thumb:hover,
   .ant-table-body::-webkit-scrollbar-thumb:hover {
      background-color: #a1a1a1 !important;
   }

   .ant-table-wrapper::-webkit-scrollbar-corner,
   .ant-table-content::-webkit-scrollbar-corner,
   .ant-table-body::-webkit-scrollbar-corner {
      background: #f1f1f1 !important;
   }

   .editable-cell {
      position: relative;
   }

   .editable-cell-value-wrap {
      padding: 5px 12px;
      cursor: pointer;
   }

   .editable-row:hover .editable-cell-value-wrap {
      padding: 4px 11px;
      border: 1px solid #d9d9d9;
      border-radius: 2px;
   }

   [data-theme='dark'] .editable-row:hover .editable-cell-value-wrap {
      border: 1px solid #434343;
   }
`
const PreviewFileImport = observer((props: any) => {
   const { t } = useTranslation()

   const { items, dataInsert, visible, onOk, onCancel, onlyView, importMode } = props
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const UserStore = useStore('UserStore')
   const OrdersStore = useStore('OrdersStore')
   const [loading, setLoading] = useState(false)

   const onFinish = async () => {
      const hasError = items.find((item: any) => item.isError)
      if (hasError) {
         showMessageError('File có lỗi, vui lòng kiểm tra lại')
         return
      }

      setLoading(true)

      const isDraftImport = importMode === 'draft'
      const result = isDraftImport
         ? await OrdersStore.insertMultipleDraftOrders(dataInsert)
         : await OrdersStore.insertMultiple(dataInsert)

      if (result?.code === 200) {
         notification.success({
            message: 'Thành công',
            description: isDraftImport ? 'Import đơn hàng nháp thành công!' : 'Import đơn hàng thành công!'
         })
         onOk()
      } else {
         showMessageError(result?.message || 'Import đơn hàng thất bại, vui lòng thử lại')
      }
      setLoading(false)
   }

   let columns = [
      {
         title: 'Xác thực',
         dataIndex: 'errorMessage',
         key: 'errorMessage',
         width: 100,
         render: (value: any) => {
            return (
               <span className="txtMax2Line" style={{ color: !value ? '#038fde' : 'red', cursor: 'pointer' }}>
                  <Tooltip placement="top" title={(value || []).join(', ')}>
                     {!value ? 'Đã xác thực' : (value || []).join(', ')}
                  </Tooltip>
               </span>
            )
         }
      },
      {
         title: 'Seller OrderID',
         dataIndex: 'sellerOrderId',
         key: 'sellerOrderId',
         width: 140,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'SKU',
         dataIndex: 'sku',
         key: 'sku',
         width: 120,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'QTY',
         dataIndex: 'qty',
         key: 'qty',
         width: 60,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'Link Mockup',
         dataIndex: 'linkMockup',
         key: 'linkMockup',
         width: 120,
         render: (value: any) => {
            return value ? (
               <a href={`${value}`} target="_blank" rel="noreferrer">
                  <span className="gx-link txtMax2Line">{value}</span>
               </a>
            ) : null
         }
      },
      {
         title: 'Link Design',
         dataIndex: 'linkDesign',
         key: 'linkDesign',
         width: 120,
         render: (value: any) => {
            return value ? (
               <a href={`${value}`} target="_blank" rel="noreferrer">
                  <span className="gx-link txtMax2Line">{value}</span>
               </a>
            ) : null
         }
      },
      {
         title: 'Note',
         dataIndex: 'note',
         key: 'note',
         width: 100,
         render: (value: any) => {
            return (
               <span className="txtMax2Line">{value && value.length > 30 ? value.slice(0, 30) + '...' : value}</span>
            )
         }
      },
      {
         title: 'Ship Label',
         dataIndex: 'shipLabel',
         key: 'shipLabel',
         width: 120,
         render: (value: any) => {
            return value ? (
               <a href={`${value}`} target="_blank" rel="noreferrer">
                  <span className="gx-link txtMax2Line">{value}</span>
               </a>
            ) : null
         }
      },
      {
         title: 'Tracking ID',
         dataIndex: 'trackingId',
         key: 'trackingId',
         width: 120,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'Customer',
         dataIndex: 'customer',
         key: 'customer',
         width: 120,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'Phone',
         dataIndex: 'phone',
         key: 'phone',
         width: 100,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'Country',
         dataIndex: 'country',
         key: 'country',
         width: 80,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'State',
         dataIndex: 'state',
         key: 'state',
         width: 80,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'Address 1',
         dataIndex: 'address1',
         key: 'address1',
         width: 150,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'Address 2',
         dataIndex: 'address2',
         key: 'address2',
         width: 120,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'City',
         dataIndex: 'city',
         key: 'city',
         width: 100,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      },
      {
         title: 'Zip Code',
         dataIndex: 'zipCode',
         key: 'zipCode',
         width: 80,
         render: (value: any) => {
            return <span className="txtMax2Line">{value}</span>
         }
      }
   ]

   return (
      <Modal
         width={'90%'}
         style={{ maxWidth: '2000px' }}
         visible={visible}
         centered
         title={'Preview file import'}
         okText={'Gửi'}
         cancelText={t(GLOBAL_CLIENT.close)}
         onCancel={onCancel}
         onOk={onFinish}
      >
         <ModalWrapper style={{ maxWidth: '2000px' }}>
            <Loading isLoading={loading}></Loading>
            <Table
               scroll={{ x: 'max-content', y: 400 }}
               columns={columns}
               dataSource={items}
               rowKey="id"
               pagination={false}
            />
         </ModalWrapper>
      </Modal>
   )
})

export default PreviewFileImport
